import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { LIGHT_PRESETS } from "./BackgroundGradient";

// Vertical rail of scene / gradient swatches anchored to the left edge.
// Designed mobile-first: compact tap targets, no wasted horizontal space,
// with a soft floating name pill that appears next to the currently-active
// swatch so the user always knows what they picked.
export const LightPicker = ({ current, onChange }) => {
  const active = LIGHT_PRESETS.find((p) => p.id === current);

  return (
    <motion.div
      initial={{ opacity: 0, x: -12, y: "-50%" }}
      animate={{ opacity: 1, x: 0, y: "-50%" }}
      transition={{ duration: 1.4, ease: "easeInOut", delay: 0.8 }}
      className="fixed left-2 sm:left-4 top-1/2 z-20 flex flex-col items-center gap-1 sm:gap-2 rounded-full backdrop-blur-xl bg-white/5 border border-white/10 px-1 sm:px-1.5 py-1.5 sm:py-2"
      style={{ paddingLeft: "max(0.25rem, env(safe-area-inset-left))" }}
      data-testid="light-picker"
      role="radiogroup"
      aria-label="Light style"
      aria-orientation="vertical"
    >
      {LIGHT_PRESETS.map((p) => {
        const isActive = current === p.id;
        return (
          <button
            key={p.id}
            role="radio"
            aria-checked={isActive}
            onClick={() => onChange(p.id)}
            title={`${p.name} — ${p.description}`}
            className={`relative w-8 h-8 sm:w-9 sm:h-9 rounded-full flex items-center justify-center transition-transform duration-500 ${
              isActive ? "scale-110" : "hover:scale-105"
            }`}
            data-testid={`light-preset-${p.id}`}
            data-active={isActive}
          >
            <span
              className={`inline-block w-5 h-5 sm:w-6 sm:h-6 rounded-full transition-all duration-500 ${
                isActive
                  ? "ring-2 ring-white/80 ring-offset-2 ring-offset-transparent"
                  : "opacity-70"
              }`}
              style={{ backgroundImage: p.swatch }}
              aria-hidden="true"
            />
          </button>
        );
      })}

      <AnimatePresence mode="wait">
        {active && (
          <motion.span
            key={active.id}
            initial={{ opacity: 0, x: -4 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -4 }}
            transition={{ duration: 0.7, ease: "easeInOut" }}
            className="pointer-events-none absolute left-full ml-3 top-1/2 -translate-y-1/2 rounded-full backdrop-blur-xl bg-white/5 border border-white/10 px-3 py-1 text-xs lowercase tracking-wider text-white/80 whitespace-nowrap"
            data-testid="light-picker-active-label"
          >
            {active.name}
          </motion.span>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default LightPicker;
